import React, { useEffect } from "react";

import { Badge, Button, ButtonGroup, Icon, IconButton, Menu } from "../../../components/ui";
import useQuoteActions from "../QuoteWorkspace/useQuoteActions";
import AssignReviewer from "./AssignReviewer";
import SelectListingModal from "./SelectListing/SelectListingModal";

// What the legacy CHOOSE ACTION button read once a quote had gone out — the status bar has no
// "sent" pill, so this badge is the only place the editor says so.
const sentLabel = (quote) => {
  if (quote.quote_expired) return "Quote expired";
  if (quote.type !== "sell") return "Quote sent";
  if (quote.quote_send_type === "SP") return "Quote sent · SP";
  if (quote.quote_send_type === "CONSIGN") return "Quote sent · Consign";
  return "Quote sent · SP & consign";
};

/**
 * The quote editor's status-bar actions (DAO setup). Same presentation and same hook as the
 * quote workspace's action bar and the quote list's row actions: the quote's ONE next step as a
 * Button, the rest behind a ⋯ Menu, and the trays an action needs (reviewer, sell send type,
 * change listing) opened under the bar.
 */
const QuoteActionsView = ({ quote, onQuoteSaved }) => {
  const actions = useQuoteActions(quote, onQuoteSaved);
  const { primary, secondary, note, panel, setPanel } = actions;

  // A new stage brings a new set of actions — close whatever tray was open.
  useEffect(() => {
    setPanel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.status, quote?.listing_id]);

  return (
    <div className="rc-quote-actions">
      <ButtonGroup align="end" stackBelow={false} className="rc-quote-actions__bar">
        {quote.status === "quote_sent" && (
          <Badge tone={quote.quote_expired ? "danger" : "info"} dot>
            {sentLabel(quote)}
          </Badge>
        )}
        {note && <span className="rc-quote-actions__note">{note}</span>}
        {primary && (
          <Button
            variant="primary"
            size="lg"
            loading={primary.loading}
            leadingIcon={<Icon name={primary.icon} size="sm" />}
            onClick={primary.onClick}
            data-btn={`quote-${primary.id}`}
          >
            {primary.label}
          </Button>
        )}
        {secondary.length > 0 && (
          <Menu
            label="Quote actions"
            items={secondary.map((a) => ({
              label: a.label,
              icon: <Icon name={a.icon} size="sm" />,
              disabled: a.disabled || a.loading,
              onSelect: a.onClick,
            }))}
            trigger={(
              <IconButton size="lg" label="More quote actions" data-btn="quote-menu-open">
                <Icon name="MoreHorizontal" size="sm" />
              </IconButton>
            )}
          />
        )}
      </ButtonGroup>

      {panel === "assign" && <AssignReviewer quote={quote} initiallyOpen />}

      {panel === "send" && (
        <ButtonGroup align="end" stackBelow={false} role="group" aria-label="Send quote as">
          {[
            ["SP", "Selling price", "quote-send-sp"],
            ["CONSIGN", "Consignment", "quote-send-consign"],
            ["SP_CONSIGN", "Both", "quote-send-both"],
          ].map(([value, label, id]) => (
            <Button
              key={value}
              variant="secondary"
              disabled={actions.sending}
              onClick={() => actions.send(value)}
              data-btn={id}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      )}

      {panel === "listing" && (
        <SelectListingModal
          productId={quote.product_id}
          currentListingId={quote.listing_id}
          isSubmitting={actions.isLinking}
          onClose={() => setPanel(null)}
          onConfirm={actions.chooseListing}
        />
      )}
    </div>
  );
};

export default QuoteActionsView;
